<script setup>
import { computed } from 'vue'

// One series over days, as a line or as bars. No library: two shapes do not
// pay for one. Each mark carries a title, so hovering names the day and value.
const props = defineProps({
  points: { type: Array, required: true }, // [{ day, value, label? }]
  kind: { type: String, default: 'line' },
  color: { type: String, required: true },
  min: { type: Number, default: 0 },
  max: { type: Number, default: null },
  format: { type: Function, default: v => String(v) },
})

const W = 520
const H = 120
const PAD = { r: 8, t: 8, b: 18 }

const values = computed(() => props.points.map(p => p.value ?? 0))
const top = computed(() => props.max ?? Math.max(1, ...values.value))
const gridValues = computed(() => [
  props.min,
  (props.min + top.value) / 2,
  top.value,
])
// The axis makes room for its widest label: "3 500 мин" needs more than "5"
const padLeft = computed(
  () =>
    10 +
    Math.max(...gridValues.value.map(v => String(props.format(v)).length)) * 5.5
)
const plotWidth = computed(() => W - padLeft.value - PAD.r)
const x = i =>
  padLeft.value + (i * plotWidth.value) / Math.max(1, props.points.length - 1)
const y = v =>
  PAD.t + (H - PAD.t - PAD.b) * (1 - (v - props.min) / (top.value - props.min))
// A day without a value breaks the line rather than dragging it to zero
const path = computed(() =>
  props.points
    .map((p, i, all) => {
      if (p.value == null) return ''
      const start = i === 0 || all[i - 1].value == null
      return `${start ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`
    })
    .join(' ')
)
const barWidth = computed(
  () => plotWidth.value / Math.max(1, props.points.length) - 2
)
const barX = i =>
  padLeft.value + (i * plotWidth.value) / Math.max(1, props.points.length)
const label = (i, step) =>
  i % step === 0 ? (props.points[i].label ?? props.points[i].day.slice(8)) : ''
const labelStep = computed(() => {
  if (props.points.length > 31) return 7
  return props.points.length > 7 ? 2 : 1
})
</script>

<template>
  <svg :viewBox="`0 0 ${W} ${H}`" class="block w-full max-w-[520px]">
    <g v-for="v in gridValues" :key="v">
      <line
        :x1="padLeft"
        :x2="W - PAD.r"
        :y1="y(v)"
        :y2="y(v)"
        class="stroke-n-weak"
        stroke-width="1"
      />
      <text
        :x="padLeft - 6"
        :y="y(v) + 4"
        font-size="10"
        text-anchor="end"
        class="fill-n-slate-11"
      >
        {{ format(v) }}
      </text>
    </g>
    <template v-if="kind === 'bars'">
      <rect
        v-for="(p, i) in points"
        :key="p.day"
        :x="barX(i) + 1"
        :y="y(p.value ?? 0)"
        :width="barWidth"
        :height="H - PAD.b - y(p.value ?? 0)"
        rx="3"
        :fill="color"
      >
        <title>{{ p.day }}: {{ format(p.value ?? 0) }}</title>
      </rect>
    </template>
    <template v-else>
      <path
        :d="path"
        fill="none"
        :stroke="color"
        stroke-width="2"
        stroke-linejoin="round"
      />
      <template v-for="(p, i) in points" :key="p.day">
        <circle
          v-if="p.value != null"
          :cx="x(i)"
          :cy="y(p.value)"
          r="6"
          fill="transparent"
        >
          <title>{{ p.day }}: {{ format(p.value) }}</title>
        </circle>
      </template>
    </template>
    <text
      v-for="(p, i) in points"
      :key="`l${p.day}`"
      :x="kind === 'bars' ? barX(i) + barWidth / 2 + 1 : x(i)"
      :y="H - 4"
      font-size="10"
      text-anchor="middle"
      class="fill-n-slate-11"
    >
      {{ label(i, labelStep) }}
    </text>
  </svg>
</template>
